import {
  parseISO,
  differenceInCalendarMonths,
  differenceInCalendarDays,
} from 'date-fns'
import {Space} from 'antd'

import styles from './UserProjectsList.module.scss'
import {
  isProjectRequest,
  PROJECT_CATEGORY_NAME,
  ProjectCategory,
  BorrowerProjectTag,
} from 'modules/project'
import {Block, Text, Box, Circle, ProgressCircle} from 'common/components'
import {
  formatDate,
  formatMoney,
  maybePluralize,
  countPercentage,
  _differenceInCalendarDays,
} from 'common/helpers'

interface UserProjectsListProps {
  projects: any[]
}

interface ItemProps {
  project: any
}

interface PeriodProps {
  isRequest: boolean
  from: Date
  to: Date
}

interface DetailsProps {
  project: any
}

export default function UserProjectsList({projects}: UserProjectsListProps) {
  return (
    <ul className={styles.root}>
      {projects.map((project) => (
        <Item key={project.uuid} project={project} />
      ))}
    </ul>
  )
}

const Item = ({project}: ItemProps) => {
  const isRequest = isProjectRequest(project)

  return (
    <Block as="li" className={styles.item} to={`/projects/${project.uuid}`}>
      <div className={styles.imageBlock}>
        <img
          className={styles.image}
          // width={404}
          height={256}
          src={project.image}
          alt={project.title}
        />
      </div>
      <div className={styles.block}>
        <h3 className={styles.title}>{project.title}</h3>
        <Box align="center" color="grey" mb={20}>
          <Space className={styles.label} size={6} split="•">
            {project.categories.map(
              (category: ProjectCategory) => PROJECT_CATEGORY_NAME[category]
            )}
          </Space>
        </Box>
        <Period
          isRequest={isRequest}
          from={parseISO(project.openDate)}
          to={parseISO(project.maturityDate)}
        />
        {isRequest ? (
          <RequestDetails project={project} />
        ) : (
          <ProjectDetails project={project} />
        )}
      </div>
    </Block>
  )
}

const Period = ({isRequest, from, to}: PeriodProps) => {
  const monthsArray = new Array(differenceInCalendarMonths(to, from) || 1).fill(null)

  const dashElements = monthsArray.map((_, index) => (
    <div key={index} className={styles.dash} />
  ))

  if (isRequest)
    return (
      <Box mb={24}>
        <div className={styles.dates}>
          <Text size="xs" weight="demiBold">
            Est. {formatDate(from, 'second')}
          </Text>
          <Text size="xs" weight="demiBold">
            Est. {formatDate(to, 'second')}
          </Text>
        </div>
        <div className={styles.line}>{dashElements}</div>
      </Box>
    )

  const today = new Date()
  const pastDays = _differenceInCalendarDays(today, from);
  const pastDaysCount = pastDays > 0 ? pastDays : 0
  const days = _differenceInCalendarDays(to, from);
  const pastDaysPercent = countPercentage(pastDaysCount, days)
  // 逾期的时间 overdue=today-maturity date
  const leftDays = pastDaysCount ? differenceInCalendarDays(to, today) : days;
  const isOverdue = leftDays < 0;

  return (
    <Box mb={24}>
      <div className={styles.dates}>
        <Text size="xs" weight="demiBold">
          {maybePluralize(pastDaysCount, 'day')}
        </Text>
        <Text size="xs" weight="medium" color={isOverdue ? 'redError' : 'grey'}>
          { isOverdue ?  `${Math.abs(leftDays)} overdue` : `${leftDays} left` }
        </Text>
      </div>
      <div className={styles.line}>
        {dashElements}
        <div className={styles.meter} style={{width: `${pastDaysPercent > 100 ? 100 : pastDaysPercent}%`}} />
      </div>
    </Box>
  )
}

const RequestDetails = ({project}: DetailsProps) => {
  return (
    <div className={styles.requestDetails}>
      <Text weight="medium">requested amount</Text>
      <Text size="4xl" mb={2}>
        {formatMoney(project.loanRequests[0].loanValueRequested, {cents: true})}
      </Text>
      <BorrowerProjectTag project={project} />
    </div>
  )
}

const ProjectDetails = ({project}: DetailsProps) => {
  return (
    <div className={styles.projectDetails}>
      <div>
        <Space size={6}>
          <Circle border color="green" />
          <Text weight="medium" mb={2}>
            current balance
          </Text>
        </Space>
        <Text size="4xl" mb={8}>
          {formatMoney(project.balance, {cents: true})}
        </Text>
        <div>
          <Space size={6}>
            <Circle border color="darkTurquoize" />
            <Text weight="medium">
              {formatMoney(project.withdrawn, {cents: true})} withdrawn
            </Text>
          </Space>
        </div>
        {Boolean(project.withdrawPending) && (
          <div>
            <Space size={6}>
              <Circle border color="lightTurquoize" />
              <Text weight="medium">
                {formatMoney(project.withdrawPending, {cents: true})} pending...
              </Text>
            </Space>
          </div>
        )}
      </div>
      <Chart project={project} />
    </div>
  )
}

const Chart = ({project}: DetailsProps) => {
  const withdrawnValue = countPercentage(project.withdrawn, project.totalAmount)

  return (
    <div className={styles.chart}>
      <ProgressCircle
        green
        value={countPercentage(project.balance, project.facilityLimit2)}
        extraValue={countPercentage(project.repaid, project.facilityLimit2)}
        radius={40}
        strokeWidth={6}
      />
      <ProgressCircle
        className={styles.progress}
        value={withdrawnValue}
        extraValue={countPercentage(
          project.withdrawPending,
          project.totalAmount
        )}
        radius={27}
        strokeWidth={6}
      >
        <Text weight="bold" size="xs" color="turquoizeDeep">
          {withdrawnValue}%
        </Text>
      </ProgressCircle>
    </div>
  )
}
